import { TablePagination } from "../../../components/ui/TablePagination";
import { FormSelect } from "../../../components/ui/select";
import { FileText } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { ContentCard } from "../../../components/ui";
import { CRM_ONBOARDING_EDIT_PATH } from "../../crm/routes";
import { extractQuotationsPage, fetchAllQuotationPages, getQuotations } from "../../../services/quotationService";
import {
  SALES_TYPES,
  STATUS_OPTIONS,
  sq,
  translateQuotationType,
  translateQuotationStatus,
  selectLatestQuotationsPerRefId,
  buildQuotationVersionHistory,
  matchesQuotationFilters,
  normalizeApiQuotation,
  filterControlClass,
} from "./quotationUtils";
import {
  StatusPill,
  QuotationPrimaryButton,
  TableSkeleton,
  EmptyState,
  ErrorState,
} from "./QuotationComponents";


const QUOTATION_LIST_PAGE_SIZE = 10;

function QuotationPage() {
  useTranslation();
  const navigate = useNavigate();
  const { search: routeSearch } = useLocation();
  const customerId = new URLSearchParams(routeSearch).get("customerId") || "";
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [quotations, setQuotations] = useState([]);
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [customerId]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(String(search || "").trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  async function loadQuotationList({ active = true } = {}) {
    const hasFilters = Boolean(
      customerId ||
      String(debouncedSearch || "").trim() ||
        String(statusFilter || "").trim() ||
        String(typeFilter || "").trim()
    );

    if (!hasFilters) {
      const response = await getQuotations({
        page,
        size: QUOTATION_LIST_PAGE_SIZE,
      });
      const pageData = extractQuotationsPage(response, {
        requestedPage: page,
        requestedSize: QUOTATION_LIST_PAGE_SIZE,
      });
      if (!active) return;
      const items = pageData.items.map(normalizeApiQuotation);
      setQuotations(items);
      setTotalPages(Math.max(1, pageData.totalPages || 1));
      setTotalElements(pageData.totalElements || items.length);
      setHasNextPage(
        Boolean(pageData.hasNext) || page + 1 < Math.max(1, pageData.totalPages || 1)
      );
      return;
    }

    const allItems = await fetchAllQuotationPages({
      size: QUOTATION_LIST_PAGE_SIZE,
      search: debouncedSearch,
      status: statusFilter,
      type: typeFilter,
    });
    if (!active) return;

    const normalized = allItems.map(normalizeApiQuotation).filter((item) => !customerId || String(item.customerId) === customerId);
    const latest = selectLatestQuotationsPerRefId(normalized);
    const filtered = latest.filter((item) =>
      matchesQuotationFilters(item, {
        search: debouncedSearch,
        statusFilter,
        typeFilter,
      })
    );
    const nextTotalElements = filtered.length;
    const nextTotalPages = Math.max(1, Math.ceil(nextTotalElements / QUOTATION_LIST_PAGE_SIZE) || 1);
    const clampedPage = Math.min(Math.max(0, page), nextTotalPages - 1);
    if (clampedPage !== page) {
      setPage(clampedPage);
      return;
    }
    const start = clampedPage * QUOTATION_LIST_PAGE_SIZE;
    setQuotations(filtered.slice(start, start + QUOTATION_LIST_PAGE_SIZE));
    setTotalPages(nextTotalPages);
    setTotalElements(nextTotalElements);
    setHasNextPage(clampedPage + 1 < nextTotalPages);
  }

  useEffect(() => {
    let active = true;

    async function loadQuotations() {
      setLoading(true);
      setLoadError("");

      try {
        await loadQuotationList({ active });
      } catch (error) {
        if (!active) return;
        setLoadError(error.message || sq("errorTitle"));
        setQuotations([]);
        setTotalPages(1);
        setTotalElements(0);
        setHasNextPage(false);
      } finally {
        if (active) setLoading(false);
      }
    }

    loadQuotations();
    return () => {
      active = false;
    };
  }, [page, debouncedSearch, statusFilter, typeFilter, customerId]);

  const latestQuotations = useMemo(
    () => selectLatestQuotationsPerRefId(quotations),
    [quotations]
  );

  const filteredQuotations = useMemo(
    () =>
      latestQuotations.filter((item) =>
        matchesQuotationFilters(item, {
          search: debouncedSearch || search,
          statusFilter,
          typeFilter,
        })
      ),
    [latestQuotations, debouncedSearch, search, statusFilter, typeFilter]
  );

  function clearFilters() {
    setSearch("");
    setStatusFilter("");
    setTypeFilter("");
    setPage(0);
  }

  function retryLoad() {
    setQuotations([]);
    setLoading(true);
    setLoadError("");
    loadQuotationList()
      .catch((error) => {
        setLoadError(error.message || sq("errorTitle"));
        setQuotations([]);
        setTotalPages(1);
        setTotalElements(0);
        setHasNextPage(false);
      })
      .finally(() => setLoading(false));
  }

  function navigateToQuotation(row) {
    const versionHistory = buildQuotationVersionHistory(quotations, row.refId);
    navigate(`/sales/quotations/quotation/${row.id ?? row.refId}`, {
      state: {
        quotation: row,
        versionHistory: versionHistory.length ? versionHistory : [row],
      },
    });
  }

  return (
    <AppLayout>
      <div className="mx-auto w-full max-w-[1280px]">
        <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <PageTitleRow back={<PageBackLink to={customerId ? CRM_ONBOARDING_EDIT_PATH(customerId) : "/"} />}>
            <div>
              <h2 className="text-[22px] font-semibold tracking-[-0.03em] text-foreground">{sq("allQuotations")}{customerId ? " · Customer " + customerId : ""}</h2>
              <p className="mt-1 max-w-3xl text-[13px] leading-6 text-[#6d7f9b]">
                {sq("listSubtitle")}
              </p>
            </div>
          </PageTitleRow>
          <QuotationPrimaryButton onClick={() => navigate("/sales/quotations/quotation/create")}>
            {sq("createQuotation")}
          </QuotationPrimaryButton>
        </div>

        {loadError && <ErrorState message={loadError} onRetry={retryLoad} />}

        <ContentCard className="overflow-hidden rounded-[14px] border border-[#d3dceb] bg-white shadow-[0_12px_28px_-24px_rgba(30,51,131,0.22)]" bodyClassName="p-0">
          <div className="flex flex-col gap-2 border-b border-[#e1e8f2] px-3 py-2.5 sm:px-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="grid w-full gap-2 lg:min-w-0 lg:flex-1 lg:grid-cols-[1.45fr_0.82fr_0.82fr]">
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] leading-none text-[#9aacbf]">⌕</span>
                <input data-page-search="true"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(0);
                  }}
                  placeholder={sq("searchPlaceholder")}
                  className={`${filterControlClass()} pl-9`}
                />
              </div>
              <FormSelect
                value={statusFilter}
                onChange={(event) => {
                  setStatusFilter(event.target.value);
                  setPage(0);
                }}
                className={filterControlClass()}
              >
                <option value="">{sq("allStatuses")}</option>
                {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{translateQuotationStatus(status)}</option>)}
              </FormSelect>
              <FormSelect
                value={typeFilter}
                onChange={(event) => {
                  setTypeFilter(event.target.value);
                  setPage(0);
                }}
                className={filterControlClass()}
              >
                <option value="">{sq("allTypes")}</option>
                {SALES_TYPES.map((type) => <option key={type} value={type}>{translateQuotationType(type)}</option>)}
              </FormSelect>
            </div>
            <div className="flex items-center justify-between gap-2 lg:min-w-[160px] lg:justify-end">
              <span className="text-[12px] text-[#8ea0bd]">{sq("countLabel", { shown: filteredQuotations.length, total: totalElements || latestQuotations.length })}</span>
              {(search || statusFilter || typeFilter) && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="h-9 rounded-[10px] border border-[#d1d9e8] px-2.5 text-[11px] font-semibold text-[#5d7598] transition hover:border-[#9eb3dc] hover:text-[#2944a8]"
                >
                  {sq("reset")}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <TableSkeleton />
          ) : latestQuotations.length === 0 && page === 0 && !(search || statusFilter || typeFilter) ? (
            <EmptyState onCreate={() => navigate("/sales/quotations/quotation/create")} />
          ) : filteredQuotations.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <p className="text-[14px] font-semibold text-[#23324c]">{sq("noFilterMatch")}</p>
              <p className="mt-1.5 text-[12px] text-[#7184a3]">{sq("noFilterMatchHint")}</p>
            </div>
          ) : (
            <>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[1100px] border-collapse">
                <thead>
                  <tr className="border-b border-[#d7dfec] text-left">
                    {[sq("refId"), sq("customerName"), sq("quotationType"), sq("version"), sq("status"), sq("sellingPrice"), sq("tenderDueDate"), sq("expiryDate"), sq("lastUpdated")].map((heading) => (
                      <th key={heading} className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.04em] text-[#96a7c2]">{heading}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                   {filteredQuotations.map((row) => (
                     <tr
                       key={`${row.refId}-${row.version}`}
                       className="cursor-pointer border-b border-[#e1e8f2] transition hover:bg-[#f8fbff] last:border-b-0"
                       onClick={() => navigateToQuotation(row)}
                     >
                      <td className="px-3 py-2 text-[12px] font-medium text-[#2f4da2]">{row.refId}</td>
                      <td className="px-3 py-2 text-[12px] font-semibold leading-5 text-[#2d394c]">
                        <span className="block max-w-[200px] truncate">{row.customer}</span>
                      </td>
                       <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{translateQuotationType(row.type)}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{row.versionLabel}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1.5">
                          <StatusPill tone={row.statusTone} label={row.status} />
                          {row.expired && <StatusPill tone="rejected" label={sq("statusExpired")} />}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-[12px] font-semibold text-[#2d394c]">{row.sellingPrice}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{row.tenderDueLabel}</td>
                      <td className="px-3 py-2 text-[12px] text-[#6f82a1]">{row.expiryLabel}</td>
                      <td className="px-3 py-2 text-[12px] text-[#9aacbf]">{row.updatedLabel}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — CRM Customer Onboarding style */}
            <div className="md:hidden divide-y divide-(--color-text-border)/35">
              {filteredQuotations.map((row) => (
                <div
                  key={`mobile-${row.refId}-${row.version}`}
                  className="p-4 space-y-3 active:bg-workspace/40 transition cursor-pointer"
                  onClick={() => navigateToQuotation(row)}
                >
                  <div className="flex justify-between items-start gap-2">
                    <h4 className="font-semibold text-foreground flex items-center gap-2 min-w-0">
                      <FileText className="h-4 w-4 text-primary shrink-0" />
                      <span className="wrap-break-word">{row.refId}</span>
                    </h4>
                    <span className="inline-flex shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-primary/10 text-primary">
                      {translateQuotationType(row.type)}
                    </span>
                  </div>

                  <div className="text-xs space-y-1.5 per-file-hint">
                    <div className="flex justify-between gap-3">
                      <span>{sq("customerName")}</span>
                      <span className="font-medium text-foreground text-right wrap-break-word">{row.customer || "—"}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("version")}</span>
                      <span className="font-medium text-foreground">{row.versionLabel}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("sellingPrice")}</span>
                      <span className="font-medium text-foreground">{row.sellingPrice}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("tenderDueDate")}</span>
                      <span className="font-medium text-foreground">{row.tenderDueLabel}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("expiryDate")}</span>
                      <span className="font-medium text-foreground">{row.expiryLabel}</span>
                    </div>
                    <div className="flex justify-between gap-3">
                      <span>{sq("lastUpdated")}</span>
                      <span className="font-medium text-foreground">{row.updatedLabel}</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5 pt-2">
                    <StatusPill tone={row.statusTone} label={row.status} />
                    {row.expired && <StatusPill tone="rejected" label={sq("statusExpired")} />}
                  </div>
                </div>
              ))}
            </div>
            </>
          )}

          {!loading && <TablePagination page={page + 1} totalPages={Math.max(totalPages, page + (hasNextPage ? 2 : 1))} totalItems={totalElements} pageSize={QUOTATION_LIST_PAGE_SIZE} onPageChange={(next) => setPage(next - 1)}  />}
        </ContentCard>
      </div>
    </AppLayout>
  );
}

export {
  QuotationPage
};
